import * as CL from '@design-system/component-library';
import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  contractPeriodMsg,
  monthMsg,
  newDeviceListMsg,
  productTypeMsg,
  statusMsg,
  t,
} from '../../common/i18n/index.js';
import { isMultiBiz } from '../../common/utils/accountUtils.js';
import {
  DEVICE_LIST_CONTRACT_PERIOD_PARAM,
  DEVICE_LIST_PRODUCT_TYPE_PARAM,
  DEVICE_LIST_STATUS_PARAM,
  DEVICE_SELECTION_PRODUCT_TYPES,
  MDM_ID_PARAM,
  getDeviceSelectionProductTypeLabel,
  getNewDeviceListPath,
  toPathWithQuery,
} from '../../common/utils/deviceListTabsUtilsCoCo.js';
import { countValues, getDeviceListStatusLabel, splitFilterValues } from '../../common/utils/deviceListUtilsCoCo.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { CompanySelector } from '../CompanySelector/CompanySelector.js';
import { getUserAccountsWithAllOption } from '../ConsolidatedListViewSelector/ConsolidatedListViewSelector.js';
import { ListSearch, SearchFilters } from '../Table/index.js';
import { resetPagingParams } from '../Table/tableUtils.js';
import type { HeaderUserAccount } from '../../common/types/authentication';
import type { DeviceListItemCoCo } from '../../common/utils/deviceListUtilsCoCo.js';
import type { SearchFilterGroup } from '../Table/SearchFilters.js';
import type { ChangeEvent, KeyboardEvent, MouseEvent } from 'react';

const getFilterGroups = (
  items: DeviceListItemCoCo[],
  filters: { productType?: string; contractPeriod?: string; status?: string }
): SearchFilterGroup[] => {
  const productTypeCounts = new Map(countValues(items.map(item => item.productType)));
  const contractPeriodCounts = countValues(items.flatMap(item => item.contractTerms)).sort(
    ([a], [b]) => Number(a) - Number(b)
  );
  const statusCounts = countValues(items.map(item => item.status).filter(Boolean));
  const selected = {
    productType: splitFilterValues(filters.productType),
    contractPeriod: splitFilterValues(filters.contractPeriod),
    status: splitFilterValues(filters.status),
  };
  return [
    {
      label: () => t.R6HP(productTypeMsg),
      value: DEVICE_LIST_PRODUCT_TYPE_PARAM,
      items: DEVICE_SELECTION_PRODUCT_TYPES.map(productType => ({
        label: () => getDeviceSelectionProductTypeLabel(productType),
        value: productType,
        itemsCount: productTypeCounts.get(productType) ?? 0,
        checked: selected.productType.includes(productType),
      })),
    },
    {
      label: () => t.KIEG(contractPeriodMsg),
      value: DEVICE_LIST_CONTRACT_PERIOD_PARAM,
      items: contractPeriodCounts.map(([term, count]) => ({
        label: () => `${term} ${t.XXVX(monthMsg)}`,
        value: term,
        itemsCount: count,
        checked: selected.contractPeriod.includes(term),
      })),
    },
    {
      label: () => t.ASQT(statusMsg),
      value: DEVICE_LIST_STATUS_PARAM,
      items: statusCounts.map(([status, count]) => ({
        label: () => getDeviceListStatusLabel(status),
        value: status,
        itemsCount: count,
        checked: selected.status.includes(status),
      })),
    },
  ].filter(group => group.items.length > 0);
};

export interface DeviceListsToolbarCoCoProps {
  // Unfiltered items, so that the filter counts don't depend on the selected filters
  items: DeviceListItemCoCo[];
}

export const DeviceListsToolbarCoCo = ({ items }: DeviceListsToolbarCoCoProps) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { authenticatedUser } = useAuth();
  const {
    [MDM_ID_PARAM]: mdmId,
    [DEVICE_LIST_PRODUCT_TYPE_PARAM]: productType,
    [DEVICE_LIST_CONTRACT_PERIOD_PARAM]: contractPeriod,
    [DEVICE_LIST_STATUS_PARAM]: status,
  } = useSearchParams<{
    [MDM_ID_PARAM]?: string;
    [DEVICE_LIST_PRODUCT_TYPE_PARAM]?: string;
    [DEVICE_LIST_CONTRACT_PERIOD_PARAM]?: string;
    [DEVICE_LIST_STATUS_PARAM]?: string;
  }>();
  const selectCompany = (_e: ChangeEvent | KeyboardEvent | MouseEvent, option: Partial<HeaderUserAccount>) => {
    const queryParams = new URLSearchParams(search);
    if (option?.accountMasterId) {
      queryParams.set(MDM_ID_PARAM, option.accountMasterId);
    } else {
      queryParams.delete(MDM_ID_PARAM);
    }
    resetPagingParams(queryParams);
    // Wait for the call stack to be empty, otherwise blur() keeps the previously selected company in the input
    setTimeout(() => {
      navigate(toPathWithQuery(pathname, queryParams.toString(), hash), { replace: true });
      inputRef.current?.blur();
    });
  };

  return (
    <div className={`of-device-lists-toolbar-coco ${dsClass.MARGIN_TOP_4}`}>
      <div className="of-device-lists-toolbar-coco__row">
        {isMultiBiz(authenticatedUser) && (
          <div className="of-device-lists-toolbar-coco__company" data-testid="device-lists-company-selector">
            <CompanySelector
              id="deviceListsCompanySelector"
              autoCompleteConfig={{ isSortable: false }}
              inputRef={inputRef}
              initialMdmId={mdmId}
              onInputChange={selectCompany}
              userAccounts={getUserAccountsWithAllOption(authenticatedUser)}
            />
          </div>
        )}
        <div className="of-device-lists-toolbar-coco__search">
          <ListSearch filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} />
        </div>
        <CL.Button
          className="of-device-lists-toolbar-coco__new-button"
          color="primary"
          onClick={() => navigate(getNewDeviceListPath(mdmId))}
        >
          {`+ ${t.MPFC(newDeviceListMsg)}`}
        </CL.Button>
      </div>
      <SearchFilters
        displayActiveFiltersAsTiles={!filtersOpen}
        filterGroups={getFilterGroups(items, { productType, contractPeriod, status })}
        onModalClose={() => setFiltersOpen(false)}
      />
    </div>
  );
};
